import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import { 
  AlertCircle, 
  AlertTriangle, 
  Clock, 
  Users, 
  Loader2,
  ExternalLink,
  ShieldAlert,
  Sprout,
  Rocket,
  ShieldCheck,
} from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useProducerAttentionSummary } from '@/hooks/useAdminProducersWithMaturity';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SystemEventLevelBadge } from '@/components/admin/SystemEventLevelBadge';
import { SystemEventSourceBadge } from '@/components/admin/SystemEventSourceBadge';
import { SystemEventDetailDialog } from '@/components/admin/SystemEventDetailDialog';
import {
  useSystemEventStats,
  useRecentSystemEvents,
  useProducersActionRequired,
  usePendingProducersCount,
  type SystemEvent,
} from '@/hooks/useSystemEvents';

type TimeRange = '24h' | '7d' | '30d';

export default function AdminDashboardPage() {
  const [range, setRange] = useState<TimeRange>('24h');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<SystemEvent | null>(null);

  const { data: stats, isLoading: statsLoading, error: statsError } = useSystemEventStats(range);
  const { data: events, isLoading: eventsLoading, error: eventsError } = useRecentSystemEvents(20, range, onlyErrors);
  const { data: actionRequired, isLoading: actionLoading } = useProducersActionRequired(range);
  const { data: pendingCount } = usePendingProducersCount();
  const { data: attSummary, isLoading: attLoading } = useProducerAttentionSummary();

  const isUnauthorized = statsError?.message === 'Unauthorized' || eventsError?.message === 'Unauthorized';

  if (isUnauthorized) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Åtkomst nekad</h2>
          <p className="text-muted-foreground mb-6">
            Du saknar behörighet att se systemloggar.
          </p>
          <Button asChild variant="outline">
            <Link to="/app/admin/producers">Tillbaka till producenter</Link>
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const formatTime = (date: string) => {
    return format(new Date(date), "d MMM HH:mm", { locale: sv });
  };

  const getRangeLabel = (range: TimeRange) => {
    switch (range) {
      case '24h': return 'senaste 24 timmarna';
      case '7d': return 'senaste 7 dagarna';
      case '30d': return 'senaste 30 dagarna';
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Översikt</h1>
            <p className="text-muted-foreground mt-1">
              Operativ status för {getRangeLabel(range)}
            </p>
          </div>

          <Select value={range} onValueChange={(v) => setRange(v as TimeRange)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Senaste 24 timmarna</SelectItem>
              <SelectItem value="7d">Senaste 7 dagarna</SelectItem>
              <SelectItem value="30d">Senaste 30 dagarna</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* KPI Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <KPICard
            title="Fel"
            value={statsLoading ? '...' : (stats?.errorCount ?? 0)}
            subtitle={getRangeLabel(range)}
            icon={AlertCircle}
            iconClassName="text-red-500"
            loading={statsLoading}
          />
          <KPICard
            title="Varningar"
            value={statsLoading ? '...' : (stats?.warnCount ?? 0)}
            subtitle={getRangeLabel(range)}
            icon={AlertTriangle}
            iconClassName="text-amber-500"
            loading={statsLoading}
          />
          <KPICard
            title="Senaste fel"
            value={
              statsLoading 
                ? '...' 
                : stats?.lastErrorAt 
                  ? formatDistanceToNow(new Date(stats.lastErrorAt), { addSuffix: true, locale: sv })
                  : 'Inga fel'
            }
            icon={Clock}
            iconClassName="text-muted-foreground"
            loading={statsLoading}
          />
          <KPICard
            title="Producenter i kö"
            value={pendingCount ?? 0}
            subtitle="Väntar på verifiering"
            icon={Users}
            iconClassName="text-blue-500"
            link="/app/admin/producers?verificationStatus=pending"
          />
        </div>

        {/* Maturity / Attention cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <KPICard
            title="Kräver åtgärd"
            value={attLoading ? '…' : (attSummary?.needsAttention ?? 0)}
            subtitle="Värdar som behöver granskning"
            icon={Sprout}
            iconClassName="text-amber-500"
            loading={attLoading}
            link="/app/admin/producers?segment=attention"
          />
          <KPICard
            title="Redo att publiceras"
            value={attLoading ? '…' : (attSummary?.readyToPublish ?? 0)}
            subtitle="Utkast med hög mognad"
            icon={Rocket}
            iconClassName="text-primary"
            loading={attLoading}
            link="/app/admin/producers?segment=draft"
          />
          <KPICard
            title="Public ej verifierad"
            value={attLoading ? '…' : (attSummary?.publicNotVerified ?? 0)}
            subtitle="Publika utan verifiering"
            icon={ShieldCheck}
            iconClassName="text-muted-foreground"
            loading={attLoading}
            link="/app/admin/producers?segment=attention"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent Events */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-base font-medium">
                  Senaste händelser
                </CardTitle>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch 
                      id="only-errors" 
                      checked={onlyErrors} 
                      onCheckedChange={setOnlyErrors}
                    />
                    <Label htmlFor="only-errors" className="text-sm">
                      Endast fel
                    </Label>
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/app/admin/system-logs">
                      Visa alla
                      <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {eventsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : events && events.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[100px]">Tid</TableHead>
                        <TableHead className="w-[80px]">Nivå</TableHead>
                        <TableHead>Meddelande</TableHead>
                        <TableHead className="w-[100px]">Producent</TableHead>
                        <TableHead className="w-[70px]">Källa</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow 
                          key={event.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedEvent(event)}
                        >
                          <TableCell className="text-xs text-muted-foreground font-mono">
                            {formatTime(event.created_at)}
                          </TableCell>
                          <TableCell>
                            <SystemEventLevelBadge level={event.level} />
                          </TableCell>
                          <TableCell className="max-w-[300px] truncate text-sm">
                            {event.message}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {event.producer?.name || '—'}
                          </TableCell>
                          <TableCell>
                            <SystemEventSourceBadge source={event.source} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    Inga händelser under perioden.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Producers needing action */}
          <div>
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium">
                  Kräver åtgärd
                </CardTitle>
              </CardHeader>
              <CardContent>
                {actionLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : actionRequired && actionRequired.length > 0 ? (
                  <div className="space-y-3">
                    {actionRequired.slice(0, 10).map((item) => (
                      <div 
                        key={item.producerId}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">
                            {item.producerName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Senast: {formatDistanceToNow(new Date(item.lastErrorAt), { addSuffix: true, locale: sv })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          <Badge variant="destructive" className="font-mono">
                            {item.errorCount}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="h-8 px-2"
                            asChild
                          >
                            <Link 
                              to={`/app/admin/system-logs?producerId=${item.producerId === 'system' ? '' : item.producerId}`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    Inga producenter kräver åtgärd.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Event Detail Dialog */}
      <SystemEventDetailDialog
        event={selectedEvent}
        open={!!selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
      />
    </AdminLayout>
  );
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  loading?: boolean;
  link?: string;
}

function KPICard({ title, value, subtitle, icon: Icon, iconClassName, loading, link }: KPICardProps) {
  const content = (
    <Card className={link ? 'hover:bg-muted/50 transition-colors cursor-pointer' : ''}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className={`h-4 w-4 ${iconClassName || 'text-muted-foreground'}`} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }

  return content;
}
